import { html, nothing, unsafeCSS } from 'lit';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import { customElement, state } from 'lit/decorators.js';
import style from './sector-diversification.scss?inline';
import { PopApiBaseComponent } from '../../base/pop-api-base-component';
import {
  Constants,
  VIEWS,
  DISPLAY_TYPE,
  INVESTMENT_TYPE,
} from './sector-diversification.constants.js';
import type { SectorDiversificationContentObject } from './sector-diversification.model.js';
import {
  dateToMMDDYYYY,
  dateStringSplit,
  dateShort,
} from '../../common/utils/formatDate';
import { sanitizeHTML } from '../../common/utils/sanitizeHTML';
import { generateLocalDisclaimerText } from '../../common/utils/generateDisclaimer';
import { SectorDiversificationFragments } from '../../common/fragments/sector-diversification.fragments.js';
import { ApiService } from '@trp-dms-digital-shared/ds-ui-library-api';

@customElement(Constants.TAG_NAME)
export class PopSectorDiversificationComponent extends PopApiBaseComponent {
  static get styles() {
    return [...super.styles, unsafeCSS(style)];
  }

  /**
   * @internal
   */
  static readonly tagName = Constants.TAG_NAME;

  /**
   * @internal
   */
  componentObject?: SectorDiversificationContentObject;

  /**
   * @internal
   */
  fragment = SectorDiversificationFragments.sectorDiversificationFragment;

  @state() view: string = Constants.DEFAULT_VIEW;
  @state() selectedTab: string = DISPLAY_TYPE.EQUITY;
  @state() equitySelectedAsOfDate: string | undefined = '';
  @state() fixedIncomeSelectedAsOfDate: string | undefined = '';

  constructor() {
    super(new ApiService());
  }

  protected processResponse(_response): void {
    this.componentObject = {
      ..._response?.historicalSectorDiversificationExhibit,
      investmentType: _response?.metadata?.category,
    };
  }

  initialize(): void {
    super.initialize();

    this.initializeAsOfDate();
  }

  private get displayType(): string {
    const { equity, fixedIncome } = this.componentObject || {};
    const hasEquity =
      (equity?.historicalSectorDiversification ?? []).length > 0;
    const hasFixedIncome =
      (fixedIncome?.historicalSectorDiversification ?? []).length > 0;

    if (hasEquity && hasFixedIncome) {
      return DISPLAY_TYPE.ALL;
    } else if (hasEquity) {
      return DISPLAY_TYPE.EQUITY;
    } else if (hasFixedIncome) {
      return DISPLAY_TYPE.FIXED_INCOME;
    } else {
      return DISPLAY_TYPE.NO_DATA;
    }
  }

  private initializeAsOfDate() {
    const availableDates = this.getAvailableAsOfDates();

    if (availableDates.length === 0) {
      return;
    }

    const setDefaultDate = (type: string, date: string) => {
      if (type === DISPLAY_TYPE.EQUITY && !this.equitySelectedAsOfDate) {
        this.equitySelectedAsOfDate = date;
      } else if (
        type === DISPLAY_TYPE.FIXED_INCOME &&
        !this.fixedIncomeSelectedAsOfDate
      ) {
        this.fixedIncomeSelectedAsOfDate = date;
      }
    };

    const defaultDate = availableDates[0];

    if (this.displayType === DISPLAY_TYPE.ALL) {
      setDefaultDate(this.selectedTab, defaultDate);
    } else {
      setDefaultDate(this.displayType, defaultDate);
    }
  }

  private getAvailableAsOfDates(): string[] {
    const dataSource = this.getDataSource();
    const uniqueDates = new Set(dataSource?.map((item) => item.asOfDate) || []);

    return Array.from(uniqueDates).sort((a, b) => b.localeCompare(a));
  }

  private getDataSource() {
    const { equity, fixedIncome } = this.componentObject || {};

    if (this.displayType === DISPLAY_TYPE.ALL) {
      return this.selectedTab === DISPLAY_TYPE.EQUITY
        ? equity?.historicalSectorDiversification
        : fixedIncome?.historicalSectorDiversification;
    }

    return this.displayType === DISPLAY_TYPE.EQUITY
      ? equity?.historicalSectorDiversification
      : fixedIncome?.historicalSectorDiversification;
  }

  private isEquitySelected() {
    if (this.displayType === DISPLAY_TYPE.ALL) {
      return this.selectedTab === DISPLAY_TYPE.EQUITY;
    }
    return this.displayType === DISPLAY_TYPE.EQUITY;
  }

  private renderTopSection() {
    return html`
      <h3
        class="sector-diversification-heading b-typography-serif--headline--100 b-font-weight-bold"
      >
        ${Constants.COMPONENT_TITLE}
      </h3>
    `;
  }

  private renderControlSection() {
    const controlTypeClass =
      this.view === VIEWS.TABLE ? 'table-controls' : 'chart-controls';

    return html`
      <div
        class="sector-diversification-controls ${controlTypeClass} b-margin-block-start--micro--jumbo"
      >
        ${this.view === VIEWS.TABLE ? this.renderAsOfDateDropdown() : nothing}
        ${this.renderViewDropdown()}
      </div>
    `;
  }

  private renderAsOfDateDropdown() {
    const availableDates = this.getAvailableAsOfDates();

    if (availableDates.length === 0) {
      return nothing;
    }

    let selectedDate: string | undefined;

    if (this.displayType === DISPLAY_TYPE.ALL) {
      selectedDate =
        this.selectedTab === DISPLAY_TYPE.EQUITY
          ? this.equitySelectedAsOfDate
          : this.fixedIncomeSelectedAsOfDate;
    } else {
      selectedDate =
        this.displayType === DISPLAY_TYPE.EQUITY
          ? this.equitySelectedAsOfDate
          : this.fixedIncomeSelectedAsOfDate;
    }

    if (availableDates.length === 1) {
      return html` <p
        data-testid="single-as-of-date"
        class="b-typography-sans-serif--caption-loose--300
                b-font-weight-light"
      >
        ${Constants.DATA_AS_OF_DROPDOWN_LABEL} ${dateShort(availableDates[0])}
      </p>`;
    }

    return html`
      <beacon-control>
        <label slot="label" for="${Constants.DATA_AS_OF_DROPDOWN_ID}">
          ${Constants.DATA_AS_OF_DROPDOWN_LABEL}
        </label>
        <select
          id="${Constants.DATA_AS_OF_DROPDOWN_ID}"
          @change="${this.asOfDateChangeHandler}"
        >
          ${availableDates.map(
            (date) => html`
              <option value="${date}" ?selected=${date === selectedDate}>
                ${dateToMMDDYYYY(date)}
              </option>
            `,
          )}
        </select>
      </beacon-control>
    `;
  }

  private asOfDateChangeHandler(
    event: Event & { target?: { value?: string } },
  ) {
    if (event.target?.value) {
      const selectedDate = dateStringSplit(event.target?.value);

      if (this.displayType === DISPLAY_TYPE.ALL) {
        if (this.selectedTab === DISPLAY_TYPE.EQUITY) {
          this.equitySelectedAsOfDate = selectedDate;
        } else if (this.selectedTab === DISPLAY_TYPE.FIXED_INCOME) {
          this.fixedIncomeSelectedAsOfDate = selectedDate;
        }
      } else if (this.displayType === DISPLAY_TYPE.EQUITY) {
        this.equitySelectedAsOfDate = selectedDate;
      } else if (this.displayType === DISPLAY_TYPE.FIXED_INCOME) {
        this.fixedIncomeSelectedAsOfDate = selectedDate;
      }
    }
  }

  private renderViewDropdown() {
    return html`
      <beacon-control>
        <label slot="label" for="${Constants.VIEW_DROPDOWN_ID}">
          ${Constants.VIEW_DROPDOWN_LABEL}
        </label>
        <select
          id="${Constants.VIEW_DROPDOWN_ID}"
          @change="${this.viewChangeHandler}"
        >
          ${Object.values(VIEWS).map(
            (view) => html`
              <option value="${view}" ?selected=${view === this.view}>
                ${view}
              </option>
            `,
          )}
        </select>
      </beacon-control>
    `;
  }

  private viewChangeHandler(event: Event & { target?: { value?: string } }) {
    const value = event.target?.value;

    if (value) {
      this.view = value;
    } else {
      console.warn(`Invalid view value: ${value}`);
    }
  }

  private renderContentSection() {
    if (this.displayType === DISPLAY_TYPE.ALL) {
      return this.renderTabbedContent();
    } else {
      return html`
        ${this.renderControlSection()} ${this.renderSubHeader()}
        ${this.renderContent()}
      `;
    }
  }

  private renderContent() {
    return this.view === VIEWS.CHART
      ? this.renderChartContent()
      : this.renderTableContent();
  }

  private renderChartContent() {
    const itemList = this.getDataSource();

    if (!itemList) {
      return html`
        <pop-no-data-available-component
          .message="${Constants.NO_CHART_DATA_AVAILABLE}"
        ></pop-no-data-available-component>
      `;
    }
    return html`
      <pop-sector-diversification-chart-component
        data-component-object="${JSON.stringify({ itemList })}"
      ></pop-sector-diversification-chart-component>
    `;
  }

  private renderTableContent() {
    const itemList = this.getDataForSelectedDate();

    if (!itemList?.sectorDiversification?.length) {
      return html`
        <pop-no-data-available-component
          .message="${Constants.NO_TABLE_DATA_AVAILABLE}"
        ></pop-no-data-available-component>
      `;
    }
    return html`
      <div class="b-margin-block-start--micro--jumbo">
        <pop-sector-diversification-table-component
          data-component-object="${JSON.stringify({
            itemList: itemList.sectorDiversification,
          })}"
        ></pop-sector-diversification-table-component>
      </div>
      ${this.renderBenchmarkName(itemList.benchmarkName || '')}
    `;
  }

  private getDataForSelectedDate() {
    const dataSource = this.getDataSource();

    let selectedDate =
      this.displayType === DISPLAY_TYPE.EQUITY
        ? this.equitySelectedAsOfDate
        : this.fixedIncomeSelectedAsOfDate;

    if (this.displayType === DISPLAY_TYPE.ALL) {
      selectedDate =
        this.selectedTab === DISPLAY_TYPE.EQUITY
          ? this.equitySelectedAsOfDate
          : this.fixedIncomeSelectedAsOfDate;
    }

    return dataSource?.find((item) => item.asOfDate === selectedDate);
  }

  private renderBenchmarkName(benchmarkName: string) {
    return html`
      <p class="sector-diversification-benchmark">
        <span
          class="b-typography-sans-serif--caption-loose--300 b-font-weight-bold"
        >
          ${Constants.BENCHMARK_PREFIX}
        </span>
        <span
          class="b-typography-sans-serif--caption-loose--300 b-font-weight-light"
        >
          ${benchmarkName}
        </span>
      </p>
    `;
  }

  private renderTabbedContent() {
    return html`
      <beacon-tabs variant="secondary" tab-type="standard">
        ${this.renderTab(Constants.EQUITY_TITLE, DISPLAY_TYPE.EQUITY, '1')}
        ${this.renderTab(
          Constants.FIXED_INCOME_TITLE,
          DISPLAY_TYPE.FIXED_INCOME,
          '2',
        )}
        ${this.renderTabPanel('1', DISPLAY_TYPE.EQUITY)}
        ${this.renderTabPanel('2', DISPLAY_TYPE.FIXED_INCOME)}
      </beacon-tabs>
    `;
  }

  private renderTab(label: string, tabType: string, value: string) {
    return html`
      <beacon-tab
        slot="tab-children"
        label="${label}"
        value="${value}"
        ?active=${this.selectedTab === tabType}
        @beacon-event-click="${() => (this.selectedTab = tabType)}"
      ></beacon-tab>
    `;
  }

  private renderTabPanel(value: string, tabType: string) {
    return html`
      <beacon-tab-panel slot="tab-panel" value="${value}">
        ${this.selectedTab === tabType
          ? html` ${this.renderControlSection()} ${this.renderContent()} `
          : nothing}
      </beacon-tab-panel>
    `;
  }

  private renderSubHeader() {
    if (this.componentObject?.investmentType !== INVESTMENT_TYPE.MULTI_ASSET) {
      return nothing;
    }

    if (this.displayType === DISPLAY_TYPE.ALL) {
      return nothing;
    }

    const title =
      this.displayType === DISPLAY_TYPE.EQUITY
        ? Constants.EQUITY_TITLE
        : Constants.FIXED_INCOME_TITLE;

    return html`
      <div class="b-margin-block-start--micro--jumbo">
        <h4
          class="sector-diversification-sub-heading b-typography-sans-serif--headline--50 b-font-weight-bold"
        >
          ${title}
        </h4>
      </div>
    `;
  }

  private renderDisclaimerSection() {
    const disclaimers = this.getDisclaimers();

    if (disclaimers.length === 0) {
      return nothing;
    }
    const disclaimerText = generateLocalDisclaimerText(disclaimers);
    return html`
      <div class="b-margin-block-start--micro--jumbo">
        <pop-disclosure-component data-footnote-indicator-type="symbol">
          ${unsafeHTML(sanitizeHTML(disclaimerText))}
        </pop-disclosure-component>
      </div>
    `;
  }

  private getDisclaimers() {
    const { equity, fixedIncome } = this.componentObject || {};

    return this.isEquitySelected()
      ? equity?.disclaimers || []
      : fixedIncome?.disclaimers || [];
  }

  render() {
    if (this.displayType === DISPLAY_TYPE.NO_DATA) {
      return html`
        ${this.renderTopSection()}
        <div class="b-margin-block-start--micro--jumbo">
          <pop-no-data-available-component
            .message="${Constants.NO_DATA_AVAILABLE}"
          ></pop-no-data-available-component>
        </div>
      `;
    }

    return this.renderComponent(html`
      <section class="sector-diversification-content b-container">
        ${this.renderTopSection()} ${this.renderContentSection()}
        ${this.renderDisclaimerSection()}
      </section>
    `);
  }
}
